import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState, Suspense } from 'react';
import { supabase, getEffectiveRole } from './lib/supabase.js';
import { useAdminStore } from './store/adminStore.js';
import type { Session } from '@supabase/supabase-js';
import { lazyPage } from './lib/lazy-page.js';
import { ChunkErrorBoundary } from './components/ui/ChunkErrorBoundary.js';

// Auth pages
const Login = lazyPage(() => import('./pages/auth/Login.js'));
const Signup = lazyPage(() => import('./pages/auth/Signup.js'));
const ForgotPassword = lazyPage(() => import('./pages/auth/ForgotPassword.js'));
const ResetPassword = lazyPage(() => import('./pages/auth/ResetPassword.js'));
const ApproveVariation = lazyPage(() => import('./pages/public/ApproveVariation.js'));
const TradeSubmit = lazyPage(() => import('./pages/public/TradeSubmit.js'));
const HandoverShare = lazyPage(() => import('./pages/public/HandoverShare.js'));
const EnquiryForm = lazyPage(() => import('./pages/public/EnquiryForm.js'));
const LegacyApprovalRedirect = lazyPage(() => import('./pages/public/LegacyApprovalRedirect.js'));

// Builder pages
const Dashboard = lazyPage(() => import('./pages/builder/Dashboard.js'));
const Projects = lazyPage(() => import('./pages/builder/Projects.js'));
const ProjectDetail = lazyPage(() => import('./pages/builder/ProjectDetail.js'));
const ProjectClientPreview = lazyPage(() => import('./pages/builder/ProjectClientPreview.js'));
const ProjectCompliance = lazyPage(() => import('./pages/builder/ProjectCompliance.js'));
const ProjectCompletion = lazyPage(() => import('./pages/builder/ProjectCompletion.js'));
const ComplianceItemDetail = lazyPage(() => import('./pages/builder/ComplianceItemDetail.js'));
const ProjectCostEntries = lazyPage(() => import('./pages/builder/ProjectCostEntries.js'));
const CostEntryDetail = lazyPage(() => import('./pages/builder/CostEntryDetail.js'));
const ProjectPayments = lazyPage(() => import('./pages/builder/ProjectPayments.js'));
const ProjectOrders = lazyPage(() => import('./pages/builder/ProjectOrders.js'));
const ProjectCommercial = lazyPage(() => import('./pages/builder/ProjectCommercial.js'));
const Quotes = lazyPage(() => import('./pages/builder/Quotes.js'));
const QuoteDetails = lazyPage(() => import('./pages/builder/QuoteDetail.js'));
const ScopeLockDetail = lazyPage(() => import('./pages/builder/ScopeLockDetail.js'));
const Invoices = lazyPage(() => import('./pages/builder/Invoices.js'));
const CreditNoteDetail = lazyPage(() => import('./pages/builder/CreditNoteDetail.js'));
const RateCardItemDetail = lazyPage(() => import('./pages/builder/RateCardItemDetail.js'));
const InvoiceDetail = lazyPage(() => import('./pages/builder/InvoiceDetail.js'));
const PaymentDetail = lazyPage(() => import('./pages/builder/PaymentDetail.js'));
const Disputes = lazyPage(() => import('./pages/builder/Disputes.js'));
const Alerts = lazyPage(() => import('./pages/builder/Alerts.js'));
const Variations = lazyPage(() => import('./pages/builder/Variations.js'));
const VariationDetail = lazyPage(() => import('./pages/builder/VariationDetail.js'));
const Settings = lazyPage(() => import('./pages/builder/Settings.js'));
const AIScopeBuilderPage = lazyPage(() => import('./pages/builder/AIScopeBuilderPage.js'));
const EstimateProposalPage = lazyPage(() => import('./pages/builder/EstimateProposalPage.js'));
const Reviews = lazyPage(() => import('./pages/builder/Reviews.js'));
const DisputeDetail = lazyPage(() => import('./pages/builder/DisputeDetail.js'));
const Issues = lazyPage(() => import('./pages/builder/Issues.js'));
const IssueDetail = lazyPage(() => import('./pages/builder/IssueDetail.js'));
const Defects = lazyPage(() => import('./pages/builder/Defects.js'));
const DefectDetail = lazyPage(() => import('./pages/builder/DefectDetail.js'));
const PortalDefect = lazyPage(() => import('./pages/portal/PortalDefect.js'));
const PhaseDetail = lazyPage(() => import('./pages/builder/PhaseDetail.js'));
const EvidenceDetail = lazyPage(() => import('./pages/builder/EvidenceDetail.js'));
const Clients = lazyPage(() => import('./pages/builder/Clients.js'));
const Pipeline = lazyPage(() => import('./pages/builder/Pipeline.js'));
const Planning = lazyPage(() => import('./pages/builder/Planning.js'));
const SiteVisits = lazyPage(() => import('./pages/builder/SiteVisits.js'));
const Trades = lazyPage(() => import('./pages/builder/Trades.js'));
const Suppliers = lazyPage(() => import('./pages/builder/Suppliers.js'));
const SupplierInvoices = lazyPage(() => import('./pages/builder/SupplierInvoices.js'));
const SiteVisitDetail = lazyPage(() => import('./pages/builder/SiteVisitDetail.js'));
const OpportunityDetail = lazyPage(() => import('./pages/builder/OpportunityDetail.js'));
const ClientDetail = lazyPage(() => import('./pages/builder/ClientDetail.js'));
const TeamMemberDetail = lazyPage(() => import('./pages/builder/TeamMemberDetail.js'));
const Team = lazyPage(() => import('./pages/builder/Team.js'));
const AuditLog = lazyPage(() => import('./pages/builder/AuditLog.js'));
const AuditEntryDetail = lazyPage(() => import('./pages/builder/AuditEntryDetail.js'));

// Admin pages
const AdminDashboard = lazyPage(() => import('./pages/admin/AdminDashboard.js'));
const TenantDetail = lazyPage(() => import('./pages/admin/TenantDetail.js'));
const UserDetail = lazyPage(() => import('./pages/admin/UserDetail.js'));

// Portal pages
const PortalHome = lazyPage(() => import('./pages/portal/PortalHome.js'));
const PortalProject = lazyPage(() => import('./pages/portal/PortalProject.js'));
const PortalProjectPhotos = lazyPage(() => import('./pages/portal/PortalProjectPhotos.js'));
const PortalQuote = lazyPage(() => import('./pages/portal/PortalQuote.js'));
const PortalEnter = lazyPage(() => import('./pages/portal/PortalEnter.js'));
const PortalPhase = lazyPage(() => import('./pages/portal/PortalPhase.js'));
const PortalInvoice = lazyPage(() => import('./pages/portal/PortalInvoice.js'));

// Inspector pages
const InspectorHome = lazyPage(() => import('./pages/inspector/InspectorHome.js'));
const InspectorPhase = lazyPage(() => import('./pages/inspector/InspectorPhase.js'));
const InspectorProjectCompliance = lazyPage(() => import('./pages/inspector/InspectorProjectCompliance.js'));
const InspectorComplianceItem = lazyPage(() => import('./pages/inspector/InspectorComplianceItem.js'));

async function fetchVerifiedRole(accessToken: string, fallbackUser: Session['user'] | null): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) {
      const { role } = await res.json();
      return role ?? null;
    }
  } catch {
    // fall through to client-side fallback
  }
  return getEffectiveRole(fallbackUser) ?? null;
}

function BuilderGuard({ children, session }: { children: React.ReactNode; session: Session | null }) {
  const { activeTenantId, verifiedRole } = useAdminStore();
  if (!session) return <Navigate to="/login" replace />;
  if (verifiedRole === 'superadmin' && !activeTenantId) return <Navigate to="/admin" replace />;
  if (verifiedRole === 'client') return <Navigate to="/portal" replace />;
  if (verifiedRole === 'inspector') return <Navigate to="/inspector" replace />;
  return <>{children}</>;
}

function PortalGuard({
  children,
  session,
  inspectorPath,
}: {
  children: React.ReactNode;
  session: Session | null;
  /** Where to send an inspector instead of their home list — see PortalPhaseRoute. */
  inspectorPath?: string;
}) {
  const { verifiedRole } = useAdminStore();
  if (!session) return <Navigate to="/login" replace />;
  if (verifiedRole === 'inspector') return <Navigate to={inspectorPath ?? '/inspector'} replace />;
  if (verifiedRole && verifiedRole !== 'client') return <Navigate to="/builder" replace />;
  return <>{children}</>;
}

// Every phase-submitted email sent before the inspector area existed deep-links here, and
// those are already in inspectors' inboxes. Carrying the id across rather than bouncing to
// the home list is what makes those links still open the phase they name.
function PortalPhaseRoute({ session }: { session: Session | null }) {
  const { id } = useParams();
  return (
    <PortalGuard session={session} inspectorPath={`/inspector/phases/${id}`}>
      <PortalPhase />
    </PortalGuard>
  );
}

function InspectorGuard({ children, session }: { children: React.ReactNode; session: Session | null }) {
  const { verifiedRole } = useAdminStore();
  if (!session) return <Navigate to="/login" replace />;
  if (verifiedRole && verifiedRole !== 'inspector') return <Navigate to="/builder" replace />;
  return <>{children}</>;
}

function SuperadminGuard({ children, session }: { children: React.ReactNode; session: Session | null }) {
  const { verifiedRole } = useAdminStore();
  if (!session) return <Navigate to="/login" replace />;
  if (verifiedRole !== 'superadmin') return <Navigate to="/builder" replace />;
  return <>{children}</>;
}

const LoadingScreen = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--light-bg)' }}>
    <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading…</div>
  </div>
);

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { setVerifiedRole, verifiedRole } = useAdminStore();

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) {
        const role = await fetchVerifiedRole(data.session.access_token, data.session.user);
        setVerifiedRole(role);
      } else {
        setVerifiedRole(null);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session) {
        const role = await fetchVerifiedRole(session.access_token, session.user);
        setVerifiedRole(role);
      } else {
        setVerifiedRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [setVerifiedRole]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/a" element={<ApproveVariation />} />
          {/* A trade's link (BC-195). No session and no guard: the token is the only credential. */}
          <Route path="/t" element={<TradeSubmit />} />
          <Route path="/handover" element={<HandoverShare />} />
          <Route path="/enquire/:slug" element={<EnquiryForm />} />
          <Route path="/a/:token" element={<LegacyApprovalRedirect />} />

          {/* Builder app */}
          <Route path="/builder" element={<BuilderGuard session={session}><Dashboard /></BuilderGuard>} />
          <Route path="/builder/projects" element={<BuilderGuard session={session}><Projects /></BuilderGuard>} />
          <Route path="/builder/projects/:id" element={<BuilderGuard session={session}><ProjectDetail /></BuilderGuard>} />
          <Route path="/builder/projects/:id/client-preview" element={<BuilderGuard session={session}><ProjectClientPreview /></BuilderGuard>} />
          <Route path="/builder/projects/:id/compliance" element={<BuilderGuard session={session}><ProjectCompliance /></BuilderGuard>} />
          <Route path="/builder/projects/:id/completion" element={<BuilderGuard session={session}><ProjectCompletion /></BuilderGuard>} />
          <Route path="/builder/compliance/:id" element={<BuilderGuard session={session}><ComplianceItemDetail /></BuilderGuard>} />
          <Route path="/builder/projects/:id/cost-entries" element={<BuilderGuard session={session}><ProjectCostEntries /></BuilderGuard>} />
          <Route path="/builder/cost-entries/:id" element={<BuilderGuard session={session}><CostEntryDetail /></BuilderGuard>} />
          <Route path="/builder/projects/:id/payments" element={<BuilderGuard session={session}><ProjectPayments /></BuilderGuard>} />
          <Route path="/builder/projects/:id/orders" element={<BuilderGuard session={session}><ProjectOrders /></BuilderGuard>} />
          <Route path="/builder/projects/:id/commercial" element={<BuilderGuard session={session}><ProjectCommercial /></BuilderGuard>} />
          <Route path="/builder/pipeline" element={<BuilderGuard session={session}><Pipeline /></BuilderGuard>} />
          <Route path="/builder/planning" element={<BuilderGuard session={session}><Planning /></BuilderGuard>} />
          <Route path="/builder/pipeline/:id" element={<BuilderGuard session={session}><OpportunityDetail /></BuilderGuard>} />
          <Route path="/builder/site-visits" element={<BuilderGuard session={session}><SiteVisits /></BuilderGuard>} />
          <Route path="/builder/site-visits/:id" element={<BuilderGuard session={session}><SiteVisitDetail /></BuilderGuard>} />
          <Route path="/builder/quotes" element={<BuilderGuard session={session}><Quotes /></BuilderGuard>} />
          <Route path="/builder/quotes/:id" element={<BuilderGuard session={session}><QuoteDetails /></BuilderGuard>} />
          <Route path="/builder/scope-locks/:projectId" element={<BuilderGuard session={session}><ScopeLockDetail /></BuilderGuard>} />
          <Route path="/builder/invoices" element={<BuilderGuard session={session}><Invoices /></BuilderGuard>} />
          <Route path="/builder/payments/:id" element={<BuilderGuard session={session}><PaymentDetail /></BuilderGuard>} />
          <Route path="/builder/invoices/:id" element={<BuilderGuard session={session}><InvoiceDetail /></BuilderGuard>} />
          <Route path="/builder/credit-notes/:id" element={<BuilderGuard session={session}><CreditNoteDetail /></BuilderGuard>} />
          <Route path="/builder/reviews" element={<BuilderGuard session={session}><Reviews /></BuilderGuard>} />
          <Route path="/builder/rate-card/:id" element={<BuilderGuard session={session}><RateCardItemDetail /></BuilderGuard>} />
          <Route path="/builder/disputes" element={<BuilderGuard session={session}><Disputes /></BuilderGuard>} />
          <Route path="/builder/disputes/:id" element={<BuilderGuard session={session}><DisputeDetail /></BuilderGuard>} />
          <Route path="/builder/issues" element={<BuilderGuard session={session}><Issues /></BuilderGuard>} />
          <Route path="/builder/issues/:id" element={<BuilderGuard session={session}><IssueDetail /></BuilderGuard>} />
          <Route path="/builder/defects" element={<BuilderGuard session={session}><Defects /></BuilderGuard>} />
          <Route path="/builder/defects/:id" element={<BuilderGuard session={session}><DefectDetail /></BuilderGuard>} />
          <Route path="/builder/alerts" element={<BuilderGuard session={session}><Alerts /></BuilderGuard>} />
          <Route path="/builder/phases/:id" element={<BuilderGuard session={session}><PhaseDetail /></BuilderGuard>} />
          <Route path="/builder/evidence/:id" element={<BuilderGuard session={session}><EvidenceDetail /></BuilderGuard>} />
          <Route path="/builder/variations" element={<BuilderGuard session={session}><Variations /></BuilderGuard>} />
          <Route path="/builder/variations/:id" element={<BuilderGuard session={session}><VariationDetail /></BuilderGuard>} />
          <Route path="/builder/clients" element={<BuilderGuard session={session}><Clients /></BuilderGuard>} />
          <Route path="/builder/clients/:id" element={<BuilderGuard session={session}><ClientDetail /></BuilderGuard>} />
          <Route path="/builder/audit" element={<BuilderGuard session={session}><AuditLog /></BuilderGuard>} />
          <Route path="/builder/audit/:id" element={<BuilderGuard session={session}><AuditEntryDetail /></BuilderGuard>} />
          <Route path="/builder/trades" element={<BuilderGuard session={session}><Trades /></BuilderGuard>} />
          <Route path="/builder/suppliers" element={<BuilderGuard session={session}><Suppliers /></BuilderGuard>} />
          <Route path="/builder/supplier-invoices" element={<BuilderGuard session={session}><SupplierInvoices /></BuilderGuard>} />
          <Route path="/builder/team" element={<BuilderGuard session={session}><Team /></BuilderGuard>} />
          <Route path="/builder/team/:userId" element={<BuilderGuard session={session}><TeamMemberDetail /></BuilderGuard>} />
          <Route path="/builder/settings" element={<BuilderGuard session={session}><Settings /></BuilderGuard>} />
          <Route path="/builder/ai-scope/:projectId" element={<BuilderGuard session={session}><AIScopeBuilderPage /></BuilderGuard>} />
          <Route path="/builder/estimate-proposals/:id" element={<BuilderGuard session={session}><EstimateProposalPage /></BuilderGuard>} />

          {/* Superadmin */}
          <Route path="/admin" element={<SuperadminGuard session={session}><AdminDashboard /></SuperadminGuard>} />
          <Route path="/admin/tenants/:id" element={<SuperadminGuard session={session}><TenantDetail /></SuperadminGuard>} />
          <Route path="/admin/users/:id" element={<SuperadminGuard session={session}><UserDetail /></SuperadminGuard>} />

          {/* Client portal */}
          {/* Where a sign-in link lands. Outside PortalGuard: no session exists yet. */}
          <Route path="/portal/enter" element={<PortalEnter />} />
          <Route path="/portal" element={<PortalGuard session={session}><PortalHome /></PortalGuard>} />
          <Route path="/portal/projects/:id" element={<PortalGuard session={session}><PortalProject /></PortalGuard>} />
          <Route path="/portal/projects/:id/photos" element={<PortalGuard session={session}><PortalProjectPhotos /></PortalGuard>} />
          <Route path="/portal/defects/:id" element={<PortalGuard session={session}><PortalDefect /></PortalGuard>} />
          <Route path="/portal/quotes/:id" element={<PortalGuard session={session}><PortalQuote /></PortalGuard>} />
          <Route path="/portal/phases/:id" element={<PortalPhaseRoute session={session} />} />
          <Route path="/portal/invoices/:id" element={<PortalGuard session={session}><PortalInvoice /></PortalGuard>} />

          {/* Inspector */}
          <Route path="/inspector" element={<InspectorGuard session={session}><InspectorHome /></InspectorGuard>} />
          <Route path="/inspector/phases/:id" element={<InspectorGuard session={session}><InspectorPhase /></InspectorGuard>} />
          <Route path="/inspector/projects/:id/compliance" element={<InspectorGuard session={session}><InspectorProjectCompliance /></InspectorGuard>} />
          <Route path="/inspector/compliance/:id" element={<InspectorGuard session={session}><InspectorComplianceItem /></InspectorGuard>} />

          {/* Default redirect — waits for verifiedRole before routing */}
          <Route path="/" element={
            !session ? <Navigate to="/login" replace />
            : !verifiedRole ? null
            : verifiedRole === 'superadmin' ? <Navigate to="/admin" replace />
            : verifiedRole === 'client' ? <Navigate to="/portal" replace />
            : verifiedRole === 'inspector' ? <Navigate to="/inspector" replace />
            : <Navigate to="/builder" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
